import { useHttpClient, useToast } from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  AppointmentProposal,
  AppointmentProposalCreate,
  AppointmentProposalUpdate,
} from './appointmentProposals.models';
import {
  AppointmentProposalErrorKind,
  appointmentProposalsReducer,
  AppointmentProposalsState,
  initState,
} from './appointmentProposals.reducer';

interface AppointmentsContextProps {
  proposals: AppointmentProposal[];
  currentProposal: AppointmentProposal | undefined;
  error: string | null;
  errorKind: AppointmentProposalErrorKind | null;
  isLoading: boolean;
  isLoaded: boolean;
  fetchAppointmentProposals: (
    appointmentId: string
  ) => Promise<AppointmentProposal[]>;
  createAppointmentProposal: (
    appointmentId: string,
    create: AppointmentProposalCreate
  ) => Promise<AppointmentProposal>;
  fetchAppointmentProposal: (
    appointmentId: string,
    appointmentProposalId: string,
    options?: { signal?: AbortSignal }
  ) => Promise<AppointmentProposal | void>;
  updateAppointmentProposal: (
    appointmentId: string,
    appointmentProposalId: string,
    update: AppointmentProposalUpdate
  ) => Promise<AppointmentProposal | void>;
  appendSelectedAppointmentProposalOptionsIds: (
    newOptionIds: string[]
  ) => Promise<AppointmentProposal | void>;
  sendProposalFollowUp: (
    appointmentId: string,
    appointmentProposalId: string,
    data: {
      followUpMessage: string;
      customerEmails: string[];
    }
  ) => Promise<void>;
  upsertProposals: (proposals: AppointmentProposal[]) => void;
  settleProposals: (
    proposals: AppointmentProposal[],
    opts?: { error?: string; errorKind?: AppointmentProposalErrorKind }
  ) => void;
  setProposalMutating: () => void;
  selectAppointmentProposals: (appointmentId: string) => void;
  selectProposalId: (
    appointmentId: string,
    appointmentProposalId: string
  ) => void;
}

export const AppointmentProposalsContext = createContext<
  AppointmentsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentProposalsState>;
}>;

export const AppointmentProposalsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(appointmentProposalsReducer, {
    ...initState,
    ...initialState,
  });
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const proposals = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter(
          (a) =>
            a.appointmentId === state.selectedAppointmentId &&
            a.status !== 'DELETED'
        ),
    [state.entities, state.ids, state.selectedAppointmentId]
  );

  const currentProposal = useMemo(() => {
    const proposal = state.entities[state.selectedId || ''];
    return proposal?.status === 'DELETED' ? undefined : proposal;
  }, [state.entities, state.selectedId]);

  const fetchAppointmentProposals: AppointmentsContextProps['fetchAppointmentProposals'] =
    useCallback(
      async (appointmentId) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSALS',
          payload: { appointmentId },
        });
        try {
          const appointmentProposals = await httpClient.get<
            AppointmentProposal[]
          >(`/appointments/${appointmentId}/proposals`);
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSALS_SUCCESS',
            payload: { appointmentProposals },
          });
          return appointmentProposals;
        } catch (error) {
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSALS_FAILURE',
            payload: { error: `${error}` },
          });
          return [];
        }
      },
      [httpClient]
    );

  const createAppointmentProposal: AppointmentsContextProps['createAppointmentProposal'] =
    useCallback(
      async (appointmentId, create) => {
        dispatch({
          type: 'CREATE_APPOINTMENT_PROPOSAL',
          payload: { appointmentId },
        });
        try {
          const appointmentProposal =
            await httpClient.post<AppointmentProposal>(
              `/appointments/${appointmentId}/proposals`,
              create
            );
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_SUCCESS',
            payload: { appointmentProposal },
          });
          return appointmentProposal;
        } catch (error) {
          dispatch({
            type: 'CREATE_APPOINTMENT_PROPOSAL_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const fetchAppointmentProposal: AppointmentsContextProps['fetchAppointmentProposal'] =
    useCallback(
      async (appointmentId, appointmentProposalId, options) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL',
          payload: { appointmentId, appointmentProposalId },
        });

        try {
          const appointmentProposal = await httpClient.get<AppointmentProposal>(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}`,
            { signal: options?.signal }
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_SUCCESS',
            payload: { appointmentProposal },
          });
          return appointmentProposal;
        } catch (error) {
          if (error instanceof Error && error.message === 'canceled') {
            dispatch({ type: 'FETCH_APPOINTMENT_PROPOSAL_CANCELLED' });
            return;
          }
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const updateAppointmentProposal: AppointmentsContextProps['updateAppointmentProposal'] =
    useCallback(
      async (appointmentId, appointmentProposalId, update) => {
        dispatch({
          type: 'UPDATE_APPOINTMENT_PROPOSAL',
          payload: {
            appointmentId,
            appointmentProposalId,
            appointmentProposal: update,
          },
        });

        try {
          const appointmentProposal = await httpClient.put<AppointmentProposal>(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}`,
            update
          );
          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_SUCCESS',
            payload: { appointmentProposal },
          });

          return appointmentProposal;
        } catch (error) {
          dispatch({
            type: 'UPDATE_APPOINTMENT_PROPOSAL_FAILURE',
            payload: { error: `${error}` },
          });
        }
      },
      [httpClient]
    );

  const sendProposalFollowUp: AppointmentsContextProps['sendProposalFollowUp'] =
    useCallback(
      async (appointmentId, appointmentProposalId, data) => {
        dispatch({
          type: 'SEND_PROPOSAL_FOLLOW_UP',
          payload: {
            appointmentId,
            appointmentProposalId,
          },
        });
        try {
          await httpClient.post(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/follow-up`,
            {
              ...data,
            }
          );
          dispatch({
            type: 'SEND_PROPOSAL_FOLLOW_UP_SUCCESS',
            payload: { appointmentProposalId },
          });
        } catch (error) {
          dispatch({
            type: 'SEND_PROPOSAL_FOLLOW_UP_FAILURE',
            payload: { error: `${error}` },
          });
        }
      },
      [httpClient]
    );

  const upsertProposals: AppointmentsContextProps['upsertProposals'] =
    useCallback((appointmentProposals) => {
      dispatch({
        type: 'UPSERT_PROPOSALS',
        payload: { appointmentProposals },
      });
    }, []);

  const selectAppointmentProposals: AppointmentsContextProps['selectAppointmentProposals'] =
    useCallback((appointmentId) => {
      dispatch({
        type: 'SET_SELECTED_APPOINTMENT',
        payload: { appointmentId },
      });
    }, []);

  const selectProposalId: AppointmentsContextProps['selectProposalId'] =
    useCallback((appointmentId, appointmentProposalId) => {
      dispatch({
        type: 'SET_SELECTED_APPOINTMENT_PROPOSAL',
        payload: { appointmentProposalId, appointmentId },
      });
    }, []);

  const setProposalMutating: AppointmentsContextProps['setProposalMutating'] =
    useCallback(() => {
      dispatch({ type: 'SET_APPOINTMENT_PROPOSAL_MUTATING' });
    }, []);

  const settleProposals: AppointmentsContextProps['settleProposals'] =
    useCallback((appointmentProposals, opts = {}) => {
      dispatch({
        type: 'SETTLE_PROPOSALS',
        payload: { appointmentProposals, ...opts },
      });
    }, []);

  const appendSelectedAppointmentProposalOptionsIds: AppointmentsContextProps['appendSelectedAppointmentProposalOptionsIds'] =
    useCallback(
      (newOptionIds) =>
        currentProposal?.presentationConfig.selectedAppointmentProposalOptionIds
          ?.length
          ? updateAppointmentProposal(
              currentProposal.appointmentId,
              currentProposal.appointmentProposalId,
              {
                presentationConfig: {
                  ...currentProposal.presentationConfig,
                  selectedAppointmentProposalOptionIds: Array.from(
                    new Set([
                      ...currentProposal.presentationConfig
                        .selectedAppointmentProposalOptionIds,
                      ...newOptionIds,
                    ])
                  ),
                },
              }
            )
          : Promise.resolve(),
      [
        currentProposal?.presentationConfig,
        currentProposal?.appointmentId,
        currentProposal?.appointmentProposalId,
        updateAppointmentProposal,
      ]
    );

  return (
    <AppointmentProposalsContext.Provider
      value={{
        fetchAppointmentProposals,
        createAppointmentProposal,
        fetchAppointmentProposal,
        updateAppointmentProposal,
        sendProposalFollowUp,
        appendSelectedAppointmentProposalOptionsIds,
        upsertProposals,
        settleProposals,
        setProposalMutating,
        selectAppointmentProposals,
        selectProposalId,
        proposals,
        currentProposal,
        error: state.error,
        errorKind: state.errorKind,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
      }}
    >
      {children}
    </AppointmentProposalsContext.Provider>
  );
};

export const useAppointmentProposals = () => {
  const context = useContext(AppointmentProposalsContext);
  if (context === undefined) {
    throw new Error(
      'useAppointmentProposals must be used within a AppointmentProposalsContext'
    );
  }
  return context;
};
